import { useInfiniteQuery } from '@tanstack/react-query';
import { listRuns } from '../services/operationsReportsApi';
import { useAuthStore } from '../store/authStore';
import type { ListRunsResponse } from '../types/operationsReports';

const PAGE_SIZE = 30;
const STALE_TIME_MS = 5 * 60 * 1000;

export function useDailyOpsRuns() {
  const userId = useAuthStore((s) => s.user?.user_id);

  const query = useInfiniteQuery<ListRunsResponse, Error, { pages: ListRunsResponse[] }, readonly unknown[], string | undefined>({
    queryKey: ['operations-reports', 'runs', userId] as const,
    queryFn: ({ pageParam }) => listRuns({ to: pageParam, limit: PAGE_SIZE }),
    initialPageParam: undefined,
    enabled: !!userId,
    getNextPageParam: (lastPage) => {
      if (lastPage.runs.length < PAGE_SIZE) return undefined;
      const oldest = lastPage.runs[lastPage.runs.length - 1].gas_day_date;
      return prevDay(oldest);
    },
    staleTime: STALE_TIME_MS,
  });

  return {
    pages: query.data?.pages ?? [],
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
  };
}

/** Subtract one day from a YYYY-MM-DD string and return YYYY-MM-DD. */
export function prevDay(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  // Use UTC noon to avoid DST edge cases, then subtract one day
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  dt.setUTCDate(dt.getUTCDate() - 1);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}
